import { useState } from 'react';
import { Button, Group, Select, Stack, Switch, Table, Text, Title } from '@mantine/core';
import { useNavigate } from 'react-router';
import {
  BOS_GOREVLENDIRME_LISTE_SUZGECI,
  etkinGorevlendirmeSuzgeciSayisi,
  useCompanies,
  useGorevlendirmeListesi,
  useIsyeriZorunluGorevlendirmeler,
  useLookup,
  useWorkplacesTumu,
  type GorevlendirmeListeSuzgeci,
  useKapilar,
} from '../api/queries';
import { tarihGoster } from '../lib/format';
import { Rozet, Sayfalama, SuzgecGostergesi, KapiSebebi } from '../sozlesme';
import { ANLAM } from '../theme';
import { GorevlendirmeListeSolukSatiri } from './gorevlendirme-parts';
import { BosListeNotu, QueryStatus } from './parts';

/**
 * B3 — **TÜR BAZLI GÖREVLENDİRME LİSTESİ** (Tur 4½ / Aşama B; Kapsam §25/8).
 *
 * > *"Bu işyerinde ilkyardımcılar kimler?"* — denetimde sorulan soru budur ve
 * > bugüne kadar cevabı **kişi kişi kart açarak** aranıyordu.
 *
 * Bu ekran aynı zamanda **ilk yardımcı oranı** açık maddesinin (`isyeri_oran`)
 * zeminidir: oran hesabı gelince sayacağı satırlar bunlardır.
 *
 * ⚠️ **YALNIZ YÜRÜRLÜKTEKİ KAYITLAR.** Kaldırılmış görevlendirme burada
 * görünmez — *"bugün kim ekipte"* sorusunun cevabı bu; *"olay tarihinde kim
 * vardı"* sorusu **kişi kartından** okunur ve orada kaldırılanlar durur
 * (§25/1). İki soruyu tek listeye sıkıştırmak, ikisini de cevapsız bırakırdı.
 *
 * ⚠️ **SAYFA SÖZLEŞMESİ P4:** sayfalama + **görünür** süzgeç; sonsuz kaydırma
 * yok, toplam görünür.
 *
 * ✏️ **GEZİNTİ ARTÇISI 2 / B1 (11.09.2026): SEKMEDEN EKLEME.** Ürün sahibi
 * holding yöneticisinde de uzmanda da ekleme satırı bulamadı — yetki kapısı
 * değil **ekran eksiğiydi**; liste yalnız okunuyordu ve boş listede *"kişinin
 * kartından verilir"* diyordu. Satır kişi kartınınkiyle **aynı bileşendir**
 * (`GorevlendirmeSolukSatiri` — önüne kişi arama hücresi eklenir); ikinci
 * tablo, ikinci yazma yolu yok. ⚠️ **P1-a ŞERHİ (sayfalanan liste):** satır
 * her sayfanın sonundadır ve üstteki düğme onu **listenin başına** alır —
 * ekipman envanterinin E1 deseni; iki satır aynı anda çizilmez.
 */
const SAYFA_BOYUTU = 25;

export function GorevlendirmelerScreen(): React.JSX.Element {
  const navigate = useNavigate();
  const companies = useCompanies();
  // ✏️ **TUR 9 / B4 (T9-18·T9-19): TEK İSTEK.** Şirket başına fan-out
  // (`useWorkplacesOf`) yerine bahçe filtreli tek uç; *"her sorgu başarılı
  // mı"* (every) ve *"hepsini birleştir"* (flatMap) desenleri de düştü —
  // istemcide birleştirilen bir listede **bir isteğin sessizce boş dönmesi
  // hiçbir yerde görünmüyordu** (madde 180'in sınıfı).
  const isyerleri = useWorkplacesTumu();
  const workplaces = isyerleri.data;
  const turler = useLookup('gorevlendirme_turu');
  // P13-b (madde 225): yazma kapısı SUNUCUDAN (`GET kapilar` — `GOREVLENDIRME_YAZMA`
  // uçla aynı sabit); kişi kartı aynı adımları kişi + arşiv ekseniyle okur (madde 226).
  const kapilar = useKapilar();
  const yazabilir = kapilar.data?.gorevlendirmeKapisi.yazabilir ?? false;

  const [suzgec, setSuzgec] = useState<GorevlendirmeListeSuzgeci>(BOS_GOREVLENDIRME_LISTE_SUZGECI);
  const liste = useGorevlendirmeListesi(suzgec);
  // 5½ / B3 — işyeri seçilince ZORUNLU SAYI özeti (§25/12): türetilir, saklanmaz.
  const zorunlu = useIsyeriZorunluGorevlendirmeler(suzgec.workplaceId);
  // B1 — soluk satırın KONUMU (E1 deseni): düğme satırı listenin başına alır.
  const [satirUstte, setSatirUstte] = useState(false);
  const [hata, setHata] = useState<string | null>(null);

  /** Süzgeç değişince sayfa 1'e döner — yoksa boş sayfada kalınır. */
  function suzgeciDegistir(yama: Partial<GorevlendirmeListeSuzgeci>): void {
    setSuzgec((eski) => ({ ...eski, ...yama, sayfa: 1 }));
  }

  const satirlar = liste.data?.satirlar ?? [];
  const toplam = liste.data?.toplam ?? 0;
  const etkinSuzgec = etkinGorevlendirmeSuzgeciSayisi(suzgec);

  return (
    <Stack>
      <Group justify="space-between" align="flex-end">
        <Stack gap={2}>
          <Title order={4}>Görevlendirmeler</Title>
          {/* §P6 — ekran ne olduğunu KENDİ söyler. */}
          <Text size="xs" c="dimmed">
            Sahadaki görev kayıtları: kim hangi işyerinde ilkyardımcı, ekip üyesi ya da operatör.
            Görevlendirme bir yetki değildir; sistemde hiçbir kapı açmaz.
          </Text>
        </Stack>
        <Group align="flex-end">
          <Select
            data-testid="gorevlendirme-isyeri-suzgeci"
            label="İşyeri"
            placeholder="Tümü"
            clearable
            searchable
            w={220}
            value={suzgec.workplaceId}
            onChange={(deger) => suzgeciDegistir({ workplaceId: deger })}
            data={(workplaces ?? []).map((w) => ({ value: w.id, label: w.ad }))}
          />
          <Select
            data-testid="gorevlendirme-tur-suzgeci"
            label="Görev"
            placeholder="Tümü"
            clearable
            searchable
            w={220}
            value={suzgec.gorevlendirmeTuruId}
            onChange={(deger) => suzgeciDegistir({ gorevlendirmeTuruId: deger })}
            data={(turler.data ?? []).map((t) => ({ value: t.id, label: t.ad }))}
          />
          {/* ⚠️ TÜRETİLMİŞ SÜZGEÇ: damga kolon değildir (§25/5) — sunucu
              satırları çözdükten SONRA süzer ve toplam da ondan sonra
              sayılır; sorguda sayıp bellekte süzmek ikisini de yalancı
              yapardı. */}
          <Switch
            data-testid="gorevlendirme-eksik-suzgeci"
            label="Yalnız eksikler"
            checked={suzgec.yalnizEksik}
            onChange={(olay) => suzgeciDegistir({ yalnizEksik: olay.currentTarget.checked })}
            mb={8}
          />
          {yazabilir && (
            <Button data-testid="gorevlendirme-ekle-ac" onClick={() => setSatirUstte(true)}>
              + Görevlendirme ekle
            </Button>
          )}
        </Group>
      </Group>

      <QueryStatus queries={[companies, isyerleri, liste, turler]} />
      {suzgec.workplaceId && zorunlu.data && zorunlu.data.satirlar.length > 0 && (
        <Table withTableBorder data-testid="zorunlu-gorevlendirme-ozeti" style={{ maxWidth: 640 }}>
          <Table.Thead>
            <Table.Tr>
              <Table.Th>Zorunlu görevlendirme</Table.Th>
              <Table.Th w={100}>Gereken</Table.Th>
              <Table.Th w={100}>Mevcut</Table.Th>
              <Table.Th w={120}>Durum</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {zorunlu.data.satirlar.map((satir) => (
              <Table.Tr key={satir.gorevlendirmeTuruId} data-testid="zorunlu-gorevlendirme-satiri">
                <Table.Td>{satir.gorevlendirmeTuruAd}</Table.Td>
                <Table.Td data-testid="zorunlu-gereken">{satir.gereken}</Table.Td>
                <Table.Td data-testid="zorunlu-mevcut">{satir.mevcut}</Table.Td>
                <Table.Td>
                  {satir.eksik > 0 ? (
                    <Rozet anlam="gecikmis" testId="zorunlu-eksik">
                      {satir.eksik} eksik
                    </Rozet>
                  ) : (
                    <Rozet anlam="olumlu" testId="zorunlu-tam">
                      Tam
                    </Rozet>
                  )}
                </Table.Td>
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
      )}
      <SuzgecGostergesi
        etkinSayisi={etkinSuzgec}
        onTemizle={() => setSuzgec(BOS_GOREVLENDIRME_LISTE_SUZGECI)}
      />

      {/* P1-a — boş listeyi soluk satır çözer; not yalnız süzgeç daraltmışken
          ya da yazamayan şapkada yazılır. */}
      {liste.isSuccess && toplam === 0 && (etkinSuzgec > 0 || !yazabilir) && (
        <BosListeNotu
          mesaj={
            etkinSuzgec > 0
              ? 'Bu süzgeçlerle eşleşen görevlendirme yok — süzgeçleri temizleyebilirsiniz.'
              : 'Bahçenizde yürürlükte görevlendirme yok.'
          }
        />
      )}

      {liste.isSuccess && (toplam > 0 || yazabilir) && (
        <Table striped highlightOnHover data-testid="gorevlendirme-listesi">
          <Table.Thead>
            <Table.Tr>
              <Table.Th>Kişi</Table.Th>
              <Table.Th>Görev</Table.Th>
              <Table.Th>Ekipman tipi</Table.Th>
              <Table.Th>İşyeri</Table.Th>
              <Table.Th>Başlangıç</Table.Th>
              <Table.Th>Durum</Table.Th>
              {yazabilir && <Table.Th w={44} aria-label="Satır eylemi" />}
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {yazabilir && satirUstte && <GorevlendirmeListeSolukSatiri onHata={setHata} />}
            {satirlar.map((satir) => (
              <Table.Tr key={satir.id} data-testid="gorevlendirme-liste-satiri">
                <Table.Td>
                  {/* P2 — satırın birincil eylemi KAYDI AÇMAKTIR; buradaki
                      kayıt kişinin kendisidir (görevlendirmenin kendi
                      sayfası yoktur — P1'in yapısal ayrımı). */}
                  <Text
                    component="a"
                    href={`/calisanlar/${satir.personId}`}
                    c={ANLAM.bilgi}
                    style={{ cursor: 'pointer' }}
                    onClick={(olay) => {
                      olay.preventDefault();
                      navigate(`/calisanlar/${satir.personId}`);
                    }}
                    data-testid="gorevlendirme-kisi-baglantisi"
                  >
                    {satir.personAdSoyad}
                  </Text>
                </Table.Td>
                <Table.Td>{satir.gorevlendirmeTuruAd}</Table.Td>
                <Table.Td>{satir.ekipmanTipiAd ?? '—'}</Table.Td>
                <Table.Td>{satir.workplaceAd}</Table.Td>
                <Table.Td>{tarihGoster(satir.baslangicTarihi)}</Table.Td>
                <Table.Td>
                  {satir.eksik ? (
                    <Group gap={6} wrap="nowrap">
                      <Rozet anlam="gecikmis" testId="gorevlendirme-liste-eksik">
                        Eksik
                      </Rozet>
                      {/* Damganın GEREKÇESİ yanında (§2.40 sunum kolu). */}
                      <Text size="xs" c="dimmed">
                        {satir.eksikSartlar.map((s) => s.ad).join(' · ')}
                      </Text>
                    </Group>
                  ) : (
                    <Rozet anlam="olumlu" testId="gorevlendirme-liste-tam">
                      Tam
                    </Rozet>
                  )}
                </Table.Td>
                {yazabilir && <Table.Td />}
              </Table.Tr>
            ))}
            {yazabilir && !satirUstte && <GorevlendirmeListeSolukSatiri onHata={setHata} />}
          </Table.Tbody>
        </Table>
      )}
      {hata && (
        <Text size="xs" c={ANLAM.engel} role="alert" data-testid="gorevlendirme-liste-hata">
          {hata}
        </Text>
      )}
      {liste.isSuccess && kapilar.data && (
        <KapiSebebi
          durum={kapilar.data.gorevlendirmeKapisi}
          nesne="Görevlendirmeyi"
          fiil="verir"
          testId="gorevlendirme-liste-yazamama-sebebi"
        />
      )}
      {toplam > 0 && (
        <Sayfalama
          sayfa={suzgec.sayfa}
          sayfaBoyutu={SAYFA_BOYUTU}
          toplam={toplam}
          onDegis={(sayfa) => setSuzgec((eski) => ({ ...eski, sayfa }))}
          testId="gorevlendirme-sayfalama"
        />
      )}
    </Stack>
  );
}
